import React, { useMemo, useState } from 'react';
import { useWBL, getNormalizedDate } from '../../context/WBLContext';
import { HeroBanner } from '../Common/HeroBanner';
import { SearchBar } from '../Common/SearchBar';
import { StudentAvatar } from '../Common/StudentAvatar';
import { formatLastNameFirst, sortStudentsByLastName } from '../../utils/studentUtils';
import { doesRecordBelongToOrg } from '../../utils/orgTeamUtils';
import { formatTime12HourEST, to24HourTime } from '../../utils/timeUtils';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  Video, 
  Phone, 
  Users, 
  ChevronLeft, 
  ChevronRight, 
  List, 
  CalendarDays,
  ExternalLink,
  Map,
  XCircle,
  Building2,
  BookmarkCheck,
  Search,
  Lock,
  X
} from 'lucide-react';
import { Interview } from '../../types';

export const InterviewsPage: React.FC = () => {
  const { 
    activeUser, 
    activeTenant, 
    interviews, 
    setCurrentView, 
    viewingStudentId, 
    selectedTerm, 
    schools, 
    allSchools,
    users,
    updateInterviewNotesAndScore 
  } = useWBL();
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [selectedDateStr, setSelectedDateStr] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Org Private Notes & Score Inline Card Editor State
  const [editingInterviewId, setEditingInterviewId] = useState<string | null>(null);
  const [inlineNotes, setInlineNotes] = useState('');
  const [inlineScore, setInlineScore] = useState<number | ''>('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const handleSaveInlineNotes = async (interviewId: string) => {
    setIsSavingNotes(true);
    try {
      const scoreVal = inlineScore === '' ? undefined : Number(inlineScore);
      await updateInterviewNotesAndScore(interviewId, inlineNotes, scoreVal);
      setEditingInterviewId(null);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Filter interviews based on role and selected term
  const userInterviews = useMemo(() => {
    if (!activeUser) return [];
    let baseInterviews = interviews;

    // Filter by term
    baseInterviews = baseInterviews.filter((i: Interview) => i.term === selectedTerm || !i.term);

    if (activeUser.role === 'student' || activeUser.role === 'parent') {
      const studentId = viewingStudentId || (activeUser.role === 'parent' ? activeUser.childId : activeUser.uid);
      return baseInterviews.filter((i: Interview) => i.studentId === studentId);
    } else if (activeUser.role === 'organization') {
      return baseInterviews.filter((i: Interview) => {
         const matchesOrg =
           (i.organizationName || '').toLowerCase().trim() === (activeUser.orgName || '').toLowerCase().trim() ||
           doesRecordBelongToOrg(i, activeUser, users);
         const matchesRegion = activeTenant.id === 'global' || !i.tenantId || i.tenantId === activeTenant.id;
         return matchesOrg && matchesRegion;
      });
    } else if (activeUser.role === 'superadmin') {
      return baseInterviews;
    } else if (activeUser.role === 'advisor') {
      if (!activeUser?.verified) {
        return [];
      }
      const referenceIds = Array.from(new Set([
        activeUser?.schoolId,
        activeUser?.pendingSchoolId,
        ...(activeUser?.schoolsServedIds || []),
        activeUser?.school,
        activeUser?.pendingSchool,
        ...(activeUser?.schoolsServed || [])
      ].filter(Boolean)));
      
      const schoolPool = allSchools && allSchools.length > 0 ? allSchools : schools || [];
      const advisorSchools = schoolPool.filter(s => {
        return referenceIds.some(ref => {
          const normRef = (ref as string).toLowerCase().trim();
          const normSchoolName = s.name.toLowerCase().trim();
          const normSchoolId = s.id.toLowerCase().trim();
          return normRef === normSchoolId || normRef === normSchoolName;
        });
      });
      
      const advisorSchoolIds = advisorSchools.map(s => s.id);
      const advisorSchoolNames = advisorSchools.map(s => s.name.toLowerCase().trim());

      return baseInterviews.filter((i) => {
        if (activeTenant.id !== 'global' && i.tenantId !== activeTenant.id) return false;
        const student = (users || []).find(u => u.uid === i.studentId);
        if (!student) return false;
        
        const ferpaGranted = typeof student.ferpaConsent === 'object'
          ? student.ferpaConsent?.granted === true
          : student.ferpaConsent === true;
        if (!ferpaGranted) return false;
        
        if (student.schoolId && advisorSchoolIds.includes(student.schoolId)) return true;
        if (student.school && advisorSchoolNames.includes(student.school.toLowerCase().trim())) return true;
        return false;
      });
    }
    const filtered = activeTenant.id === 'global' ? baseInterviews : baseInterviews.filter((i: Interview) => i.tenantId === activeTenant.id);

    if (!searchQuery.trim()) return filtered;
    const q = searchQuery.toLowerCase().trim();
    return filtered.filter(i => 
      (i.studentName || "").toLowerCase().includes(q) ||
      (i.opportunityTitle || "").toLowerCase().includes(q) ||
      (i.organizationName || "").toLowerCase().includes(q) ||
      (i.person || "").toLowerCase().includes(q)
    );
  }, [interviews, activeUser, activeTenant.id, selectedTerm, viewingStudentId, schools, users, searchQuery]);

  const upcomingInterviews = useMemo(() => {
    return userInterviews
      .filter((i: Interview) => i.status === 'scheduled')
      .sort((a: Interview, b: Interview) => new Date(`${getNormalizedDate(a.date)}T${to24HourTime(a.time)}`).getTime() - new Date(`${getNormalizedDate(b.date)}T${to24HourTime(b.time)}`).getTime());
  }, [userInterviews]);

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'virtual': return <Video className="w-4 h-4 text-blue-500" />;
      case 'in_person': return <MapPin className="w-4 h-4 text-emerald-500" />;
      case 'phone': return <Phone className="w-4 h-4 text-amber-500" />;
      default: return <Users className="w-4 h-4 text-slate-500" />;
    }
  };

  const getModeLabel = (interview: Interview) => {
    switch (interview.mode) {
      case 'virtual': return `Virtual (${interview.link || 'Link attached'})`;
      case 'in_person': return `In Person (${interview.address || 'Address provided'})`;
      case 'phone': return `Phone (${interview.phone || 'Phone number provided'})`;
      default: return 'TBD';
    }
  };

  const handlePrevMonth = () => {
    setSelectedMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setSelectedDateStr(null);
  };

  const handleNextMonth = () => {
    setSelectedMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setSelectedDateStr(null);
  };

  const handleToday = () => {
    setSelectedMonth(new Date());
    setSelectedDateStr(null);
  };

  // Calendar Grid Construction
  const calendarGrid = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay(); // 0 (Sun) - 6 (Sat)
    const totalDays = new Date(year, month + 1, 0).getDate();
    const prevMonthTotalDays = new Date(year, month, 0).getDate();
    
    interface CalendarCell {
      dateStr: string;
      dayNum: number;
      isCurrentMonth: boolean;
      interviews: Interview[];
    }
    
    const grid: CalendarCell[] = [];
    
    // Fill previous month padding days
    for (let i = startDay - 1; i >= 0; i--) {
      const d = prevMonthTotalDays - i;
      const m = month === 0 ? 11 : month - 1;
      const y = month === 0 ? year - 1 : year;
      const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayInterviews = upcomingInterviews.filter((item: Interview) => getNormalizedDate(item.date) === dateStr);
      grid.push({
        dateStr,
        dayNum: d,
        isCurrentMonth: false,
        interviews: dayInterviews
      });
    }
    
    // Fill current month days
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayInterviews = upcomingInterviews.filter((item: Interview) => item.date === dateStr);
      grid.push({
        dateStr,
        dayNum: d,
        isCurrentMonth: true,
        interviews: dayInterviews
      });
    }
    
    // Fill next month padding days (ensure complete grid rows, standard 42 cells)
    const remainingCells = 42 - grid.length;
    for (let d = 1; d <= remainingCells; d++) {
      const m = month === 11 ? 0 : month + 1;
      const y = month === 11 ? year + 1 : year;
      const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayInterviews = upcomingInterviews.filter((item: Interview) => item.date === dateStr);
      grid.push({
        dateStr,
        dayNum: d,
        isCurrentMonth: false,
        interviews: dayInterviews
      });
    }
    
    return grid;
  }, [selectedMonth, upcomingInterviews]);

  const selectedDayInterviews = useMemo(() => {
    if (!selectedDateStr) return [];
    return upcomingInterviews.filter((i: Interview) => getNormalizedDate(i.date) === selectedDateStr);
  }, [selectedDateStr, upcomingInterviews]);

  const activeMonthInterviews = useMemo(() => {
    const targetYear = selectedMonth.getFullYear();
    const targetMonth = selectedMonth.getMonth();
    return upcomingInterviews.filter((i: Interview) => {
      const parts = i.date.split('-');
      if (parts.length !== 3) return false;
      const yr = parseInt(parts[0], 10);
      const mo = parseInt(parts[1], 10) - 1; // 0-indexed
      return yr === targetYear && mo === targetMonth;
    });
  }, [selectedMonth, upcomingInterviews]);

  return (
    <div className="space-y-6 pb-12">
      <HeroBanner 
        title="Interviews"
        subtitle="Manage and view your work-based learning interviews"
        pageKey="interviews"
      />

      <div className="w-[95%] mx-auto mt-6 space-y-4">
        {activeUser?.role === 'organization' && editingInterviewId && userInterviews.find(i => i.id === editingInterviewId) ? (
          (() => {
            const interview = userInterviews.find(i => i.id === editingInterviewId)!;
            const dateObj = new Date(getNormalizedDate(interview.date) + "T12:00:00");
            return (
              <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xs space-y-8 animate-in fade-in duration-300" id="full-page-evaluation-workspace">
                {/* Workspace Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-6">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="bg-blue-100 text-[#0094c8] text-[10px] font-black uppercase px-2.5 py-1 rounded-full flex items-center gap-1.5 font-mono">
                        <Lock className="w-3.5 h-3.5" /> Private Org Eval
                      </span>
                    </div>
                    <h2 className="text-2xl font-black text-[#0f2d4a] uppercase tracking-tight">
                      Evaluation Workspace
                    </h2>
                    <p className="text-xs text-slate-500 font-medium">
                      Feedback and scores entered here are strictly private, safe, and stored securely in your organization vault.
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setEditingInterviewId(null)}
                      className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-[#0f2d4a] text-xs font-black uppercase tracking-wider rounded-xl border border-slate-200 transition-all cursor-pointer"
                    >
                      Cancel & Exit
                    </button>
                    <button
                      onClick={() => handleSaveInlineNotes(interview.id)}
                      disabled={isSavingNotes}
                      className="px-6 py-2.5 bg-[#0f2d4a] hover:bg-[#1a4066] text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all disabled:bg-slate-300 flex items-center gap-2 cursor-pointer shadow-xs hover:shadow-md"
                    >
                      {isSavingNotes ? 'Saving...' : 'Save Evaluation'}
                    </button>
                  </div>
                </div>

                {/* Main Split Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  
                  {/* Left Column: Candidate & Interview metadata context card (span 4 of 12) */}
                  <div className="lg:col-span-4 bg-[#F4F7FA] border border-slate-200/50 rounded-3xl p-6 space-y-6">
                    <div>
                      <span className="text-[10px] font-black tracking-widest text-[#0094c8] uppercase block mb-1 font-mono">
                        Candidate
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-white text-[#0f2d4a] flex items-center justify-center text-lg font-black uppercase border border-slate-200 shadow-2xs">
                          {interview.studentName.charAt(0)}
                        </div>
                        <div>
                          <h3 className="text-base font-black text-[#0f2d4a]">
                            {interview.studentName}
                          </h3>
                          <p className="text-xs text-slate-500 font-bold font-mono">
                            {(users || []).find(u => u.uid === interview.studentId || u.email === interview.studentEmail)?.email || interview.studentEmail || 'Candidate'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-200/60 pt-4 space-y-4">
                      <div>
                        <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase block font-mono">
                          Opportunity Target
                        </span>
                        <h4 className="text-sm font-black text-[#0f2d4a] uppercase tracking-tight mt-0.5">
                          {interview.opportunityTitle}
                        </h4>
                        <p className="text-xs font-bold text-[#0094c8] flex items-center gap-1 mt-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {interview.organizationName}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase block font-mono">
                            Date
                          </span>
                          <span className="text-xs font-bold text-slate-700 block mt-0.5">
                            {dateObj.toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase block font-mono">
                            Time Slot
                          </span>
                          <span className="text-xs font-bold text-slate-700 block mt-0.5 font-mono">
                            {formatTime12HourEST(interview.time)}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 font-sans">
                        <div>
                          <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase block font-mono">
                            Interviewer
                          </span>
                          <span className="text-xs font-bold text-slate-700 block mt-0.5">
                            {interview.person}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase block font-mono">
                            Setting
                          </span>
                          <span className="text-xs font-bold text-slate-700 block mt-0.5 capitalize">
                            {interview.mode}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-2xl space-y-2 text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-blue-800">
                        <Lock className="w-4 h-4 text-blue-600" />
                        <span>Security & Visibility</span>
                      </div>
                      <p className="text-slate-600 font-medium leading-relaxed font-sans">
                        This feedback card is protected by tenant access policies. School faculty, mentors, parents, and candidates cannot view or query these metrics.
                      </p>
                    </div>
                  </div>

                  {/* Right Column: Dynamic scoring and detailed comments form input (span 8 of 12) */}
                  <div className="lg:col-span-8 space-y-6">
                    {/* Performance Score Section */}
                    <div className="bg-slate-50 border border-slate-200/60 p-6 rounded-3xl space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider">
                            Candidate Evaluation Score
                          </h4>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Provide an overall presentation rating from 0 to 100.
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input 
                            type="number"
                            min="0"
                            max="100"
                            value={inlineScore}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                setInlineScore('');
                              } else {
                                const num = Math.min(100, Math.max(0, parseInt(val) || 0));
                                setInlineScore(num);
                              }
                            }}
                            placeholder="E.g. 85"
                            className="w-20 px-3 py-2 bg-white border border-slate-300 rounded-xl text-center font-black text-[#0094c8] outline-hidden focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all text-sm font-mono"
                          />
                          <span className="text-xs font-black text-slate-400 uppercase font-mono">/ 100</span>
                        </div>
                      </div>

                      {/* Giant Slider bar */}
                      <div className="space-y-2 pt-2">
                        <input 
                          type="range"
                          min="0"
                          max="100"
                          value={inlineScore === '' ? 50 : inlineScore}
                          onChange={(e) => setInlineScore(Number(e.target.value))}
                          className="w-full accent-[#0094c8] h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">
                          <span>0 (No Fit)</span>
                          <span>50 (Average)</span>
                          <span>75 (Strong Match)</span>
                          <span>100 (Exceptional)</span>
                        </div>
                      </div>

                      {/* Presets Grid */}
                      <div className="pt-2">
                        <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider block mb-2 font-mono">
                          Quick Presets:
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: 'Unsatisfactory (50)', val: 50 },
                            { label: 'Meets Expectations (75)', val: 75 },
                            { label: 'Strong Candidate (85)', val: 85 },
                            { label: 'Excellent Fit (95)', val: 95 },
                            { label: 'Outstanding (100)', val: 100 },
                          ].map((preset) => (
                            <button
                              key={preset.val}
                              onClick={() => setInlineScore(preset.val)}
                              className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg border transition-all cursor-pointer ${
                                inlineScore === preset.val
                                  ? 'bg-[#0094c8]/10 text-[#0094c8] border-[#0094c8]/40'
                                  : 'bg-white hover:bg-slate-100 text-slate-600 border-slate-200'
                              }`}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Evaluation Notes Input Section */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider">
                            Detailed Evaluation Notes
                          </h4>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Summarize your observations regarding communication, technical skills, cultural fit, and follow-ups.
                          </p>
                        </div>
                        <span className="text-[10px] text-slate-400 font-bold font-mono">
                          {inlineNotes.length} / 1000 CHARS
                        </span>
                      </div>

                      <textarea
                        rows={12}
                        maxLength={1000}
                        value={inlineNotes}
                        onChange={(e) => setInlineNotes(e.target.value)}
                        placeholder="Type or paste evaluation remarks here..."
                        className="w-full p-5 bg-white border border-slate-200 rounded-3xl text-sm font-medium text-slate-700 outline-hidden focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all resize-none leading-relaxed font-sans"
                      />
                    </div>
                  </div>

                </div>
              </div>
            );
          })()
        ) : (
          <>
            {/* Combined Search bar and View Toggles */}
            <div className="bg-white rounded-xl border border-slate-200/70 p-3 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3 w-full">
              <div className="flex-1 w-full">
                <SearchBar 
                  value={searchQuery} 
                  onChange={setSearchQuery} 
                  placeholder="Search interviews (student, opportunity, organization)..." 
                />
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50/50 border border-blue-100/50 rounded-xl shrink-0 self-start sm:self-auto">
                <span className="text-[10px] font-black text-[#0094c8] uppercase tracking-widest font-mono">
                  {userInterviews.length} Total Interviews
                </span>
              </div>
            </div>

            {/* View Toggles on the right */}
            <div className="bg-slate-100 p-1 rounded-xl flex items-center border border-slate-200 gap-1 shrink-0 self-start md:self-auto">
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                  viewMode === 'calendar' 
                    ? 'bg-white text-[#0094c8] shadow-sm' 
                    : 'text-slate-500 hover:text-slate-800 hover:bg-white/50'
                }`}
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Calendar View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white text-[#0094c8] shadow-sm' 
                    : 'text-slate-500 hover:text-slate-800 hover:bg-white/50'
                }`}
              >
                <List className="w-3.5 h-3.5" />
                List View
              </button>
            </div>
          </div>
        
        {/* Empty State Banner */}
        {userInterviews.length === 0 && !searchQuery ? (
          <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center shadow-sm space-y-6 max-w-xl mx-auto my-12">
            <div className="mx-auto w-16 h-16 bg-blue-50 text-[#0094c8] rounded-full flex items-center justify-center">
              <CalendarIcon className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">No Upcoming Interviews</h3>
              <p className="text-sm text-slate-500 mt-2 font-sans">
                There are no work-based learning interviews currently scheduled on this platform.
              </p>
            </div>
            {activeUser?.role === 'student' && (
              <button 
                onClick={() => setCurrentView('applications')}
                className="px-6 py-3 bg-[#0094c8] hover:bg-[#007ba6] text-white text-xs font-black rounded-xl shadow-md transition-all uppercase tracking-widest"
              >
                Track Your Applications
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {userInterviews.length === 0 && searchQuery ? (
              <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center shadow-sm space-y-4">
                <div className="mx-auto w-12 h-12 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center">
                  <Search className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">No matching interviews</h3>
                  <p className="text-sm text-slate-500">Try adjusting your search query to find what you're looking for.</p>
                </div>
                <button 
                  onClick={() => setSearchQuery('')}
                  className="text-[#0094c8] text-xs font-black uppercase tracking-widest hover:underline"
                >
                  Clear Search
                </button>
              </div>
            ) : null}

            {/* 1. CALENDAR VIEW MODE */}
            {viewMode === 'calendar' && userInterviews.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                
                {/* Left Panel: Calendar Grid (span 7 of 12) */}
                <div className="lg:col-span-7 bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
                  
                  {/* Calendar Navigation and Title */}
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-black text-[#0f2d4a] uppercase tracking-tight">
                        {selectedMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
                      </h2>
                      <span className="bg-blue-100 text-[#0094c8] text-[10px] font-black uppercase px-2 py-0.5 rounded-full font-mono">
                        {activeMonthInterviews.length} Scheduled
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button 
                        onClick={handlePrevMonth}
                        className="p-1.5 hover:bg-slate-100 border border-slate-200 rounded-lg text-slate-600 transition-colors"
                        title="Prior Month"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={handleToday}
                        className="px-3 py-1.5 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-black uppercase text-slate-700 transition-colors"
                      >
                        Today
                      </button>
                      <button 
                        onClick={handleNextMonth}
                        className="p-1.5 hover:bg-slate-100 border border-slate-200 rounded-lg text-slate-600 transition-colors"
                        title="Next Month"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Calendar Matrix */}
                  <div className="grid grid-cols-7 gap-1 text-center font-mono">
                    {/* Days of Week Row */}
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                      <div key={idx} className="text-[10px] font-black tracking-widest text-slate-400 py-2">
                        {day}
                      </div>
                    ))}

                    {/* Cells Grid */}
                    {calendarGrid.map((cell, idx) => {
                      const hasInterviews = cell.interviews.length > 0;
                      const isSelected = selectedDateStr === cell.dateStr;
                      const isToday = cell.dateStr === new Date().toISOString().split('T')[0];

                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedDateStr(cell.dateStr);
                          }}
                          className={`min-h-[4.5rem] rounded-xl flex flex-col items-center justify-between p-1.5 border transition-all text-left relative ${
                            cell.isCurrentMonth 
                              ? 'bg-slate-50/30' 
                              : 'bg-slate-100/30 text-slate-400 border-transparent opacity-60'
                          } ${
                            isToday ? 'border-[#0094c8]' : 'border-slate-100'
                          } ${
                            isSelected 
                              ? 'bg-[#0094c8]/10 border-[#0094c8]/50 ring-2 ring-[#0094c8]/10' 
                              : 'hover:bg-slate-100/80 hover:border-slate-200'
                          }`}
                        >
                          <span className={`text-xs font-black px-1.5 py-0.5 rounded-md ${
                            isToday ? 'bg-[#0094c8] text-white' : ''
                          } ${
                            isSelected && !isToday ? 'text-[#0094c8]' : 'text-slate-700'
                          } ${
                            !cell.isCurrentMonth && 'text-slate-400'
                          }`}>
                            {cell.dayNum}
                          </span>

                          <div className="w-full flex flex-col gap-0.5 items-stretch mt-1.5">
                            {hasInterviews && (
                              <div className="w-full h-1 bg-[#0094c8] rounded" />
                            )}
                            {cell.interviews.slice(0, 1).map((i) => (
                              <div 
                                key={i.id} 
                                className="hidden md:block truncate text-[8px] font-black uppercase text-slate-700 mt-1 leading-none"
                              >
                                {i.opportunityTitle}
                              </div>
                            ))}
                            {cell.interviews.length > 1 && (
                              <div className="hidden md:block text-[8px] font-semibold text-[#0094c8] mt-0.5 uppercase tracking-tight">
                                +{cell.interviews.length - 1} more
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Right Panel: Daily Schedule Overview (span 5 of 12) */}
                <div className="lg:col-span-5 bg-white border border-slate-200 rounded-3xl p-6 shadow-xs flex flex-col justify-between">
                  <div>
                    {/* Schedule Header */}
                    <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
                      <div className="space-y-0.5">
                        <h3 className="text-sm font-black text-[#0f2d4a] uppercase tracking-wider">
                          {selectedDateStr ? "Day Schedule" : "All Scheduled Program"}
                        </h3>
                        <p className="text-xs text-slate-500 font-sans">
                          {selectedDateStr ? (
                            new Date(selectedDateStr + 'T12:00:00').toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                          ) : (
                            "Showing all current upcoming schedules"
                          )}
                        </p>
                      </div>
                      
                      {selectedDateStr && (
                        <button
                          onClick={() => setSelectedDateStr(null)}
                          className="flex items-center gap-1 text-[10px] uppercase font-black tracking-widest text-[#0094c8] hover:text-[#007ba6]"
                        >
                          Show All
                        </button>
                      )}
                    </div>

                    {/* Filtered Interviews Display List */}
                    <div className="space-y-4 max-h-[30rem] overflow-y-auto pr-1">
                      {selectedDateStr ? (
                        selectedDayInterviews.length === 0 ? (
                          <div className="text-center py-16 space-y-3">
                            <XCircle className="w-8 h-8 text-slate-300 mx-auto" />
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
                              No meetings scheduled
                            </p>
                            <p className="text-xs text-slate-400 px-4">
                              There are no interviews scheduled on this calendar date.
                            </p>
                          </div>
                        ) : (
                          selectedDayInterviews.map((interview: Interview) => (
                            <div 
                              key={interview.id} 
                              className="p-4 rounded-2xl bg-slate-50 border border-slate-200/50 hover:border-slate-300 transition-all space-y-4 shadow-2xs"
                            >
                              <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                  <div className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 text-[8px] font-black uppercase inline-block">
                                    {formatTime12HourEST(interview.time)}
                                  </div>
                                  <h4 className="text-sm font-black text-[#0f2d4a] uppercase leading-tight">
                                    {interview.opportunityTitle}
                                  </h4>
                                  <p className="text-xs font-bold text-[#0094c8] flex items-center gap-1">
                                    <Building2 className="w-3.5 h-3.5" />
                                    {interview.organizationName}
                                  </p>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3 text-xs border-y border-slate-200/40 py-3">
                                <div>
                                  <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider block">Candidate</span>
                                  <span className="font-bold text-slate-700 block mt-0.5">{formatLastNameFirst(interview.studentName, (users || []).find(u => u.uid === interview.studentId || u.email === interview.studentEmail))}</span>
                                </div>
                                <div>
                                  <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider block">Interviewer</span>
                                  <span className="font-bold text-slate-700 block mt-0.5">{interview.person}</span>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 text-xs text-slate-600">
                                {getModeIcon(interview.mode)}
                                <span className="font-medium line-clamp-1">{getModeLabel(interview)}</span>
                              </div>

                              {interview.mode === 'virtual' && interview.link && (
                                activeUser?.role === 'advisor' ? (
                                  <div 
                                    className="w-full py-2.5 bg-slate-100 text-slate-500 text-xs font-bold uppercase tracking-wider rounded-xl flex items-center justify-center gap-1.5 border border-slate-200"
                                    title="Advisor View: Virtual meeting link is reserved for the candidate and interviewer"
                                  >
                                    <Video className="w-3.5 h-3.5 text-slate-400" />
                                    Virtual (Host & Student Only)
                                  </div>
                                ) : (
                                  <a 
                                    href={interview.link} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="w-full py-2.5 bg-[#0f2d4a] hover:bg-[#1a4066] text-white text-xs font-black uppercase tracking-wider rounded-xl transition-colors flex items-center justify-center gap-1.5 shadow-xs"
                                  >
                                    <Video className="w-3.5 h-3.5" />
                                    Join Meeting Room
                                  </a>
                                )
                              )}

                              {activeUser?.role === "organization" && (
                                <div className="space-y-3 pt-2 border-t border-slate-100">
                                  {(interview.orgNotes || interview.orgScore !== undefined) && (
                                    <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-xl space-y-1.5 text-xs">
                                      <div className="flex items-center justify-between font-bold text-[#0f2d4a]">
                                        <span className="text-[9px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                                          <Lock className="w-3 h-3 text-slate-400" /> Private Org Eval
                                        </span>
                                        {interview.orgScore !== undefined && (
                                          <span className="bg-[#0094c8] text-white text-[10px] px-2 py-0.5 rounded-full">
                                            Score: {interview.orgScore}/100
                                          </span>
                                        )}
                                      </div>
                                      {interview.orgNotes && (
                                        <p className="text-slate-600 italic font-medium leading-relaxed">
                                          "{interview.orgNotes}"
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  <button
                                    onClick={() => {
                                      setEditingInterviewId(interview.id);
                                      setInlineNotes(interview.orgNotes || "");
                                      setInlineScore(interview.orgScore !== undefined ? interview.orgScore : "");
                                    }}
                                    className="w-full py-2.5 bg-[#F4F7FA] hover:bg-slate-200 text-[#0f2d4a] text-xs font-black uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 border border-slate-200/60 cursor-pointer"
                                  >
                                    <BookmarkCheck className="w-3.5 h-3.5 text-blue-600" />
                                    {interview.orgNotes || interview.orgScore !== undefined ? "Edit Eval & Notes" : "Add Private Eval & Notes"}
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        )
                      ) : (
                        upcomingInterviews.map((interview: Interview) => {
                          const dateObj = new Date(getNormalizedDate(interview.date) + "T12:00:00");
                          return (
                            <div 
                              key={interview.id} 
                              className="p-4 rounded-2xl bg-white border border-slate-100 hover:border-[#0094c8]/30 transition-all space-y-3 cursor-pointer flex flex-col justify-between"
                              onClick={() => setSelectedDateStr(interview.date)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="space-y-0.5">
                                  <span className="text-[8px] font-black tracking-widest text-[#0094c8] uppercase block">
                                    {dateObj.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })} @ {formatTime12HourEST(interview.time)}
                                  </span>
                                  <h4 className="text-xs font-black text-[#0f2d4a] uppercase">
                                    {interview.opportunityTitle}
                                  </h4>
                                  <p className="text-[11px] font-bold text-slate-500">
                                    {formatLastNameFirst(interview.studentName, (users || []).find(u => u.uid === interview.studentId || u.email === interview.studentEmail))} &bull; {interview.organizationName}
                                  </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-300" />
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-4 mt-4 bg-slate-50/50 -mx-6 -mb-6 p-6 rounded-b-3xl">
                    <p className="text-[10px] text-slate-400 font-mono tracking-wide uppercase">
                      TIP: Click any highlighted date in the calendar to reveal its specific agenda and timeslot details.
                    </p>
                  </div>

                </div>

              </div>
            )}

            {/* 2. FULL WIDTH LIST VIEW MODE */}
            {viewMode === 'list' && userInterviews.length > 0 && (
              <div className="space-y-4">
                {/* Responsive spacious table/list with full-width horizontal rows */}
                <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
                  
                  {/* Table headers (hidden on mobile, beautifully formatted on desktop) */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-8 py-4 bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-400 font-mono">
                    <div className="col-span-2">Date & Time</div>
                    <div className="col-span-3">Opportunity & Organization</div>
                    <div className="col-span-2">Candidate</div>
                    <div className="col-span-2">Interviewer</div>
                    <div className="col-span-2">Meeting Type & Settings</div>
                    <div className="col-span-1 text-right">Actions</div>
                  </div>

                  {/* Table rows list */}
                  <div className="divide-y divide-slate-100">
                    {upcomingInterviews.map((interview: Interview) => {
                      const dateObj = new Date(getNormalizedDate(interview.date) + "T12:00:00");
                      
                      return (
                        <div key={interview.id} className="flex flex-col hover:bg-slate-50/30 transition-all">
                          {/* Main Row Content */}
                          <div 
                            className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center px-8 py-5"
                          >
                            {/* Col 1: Date & Time */}
                            <div className="col-span-2 flex items-center gap-3">
                              <div className="p-2.5 bg-blue-50 text-[#0094c8] rounded-xl flex flex-col items-center justify-center min-w-[3.5rem] border border-blue-100/20">
                                <span className="text-[9px] font-black font-mono tracking-widest uppercase">
                                  {dateObj.toLocaleDateString('default', { month: 'short' })}
                                </span>
                                <span className="text-base font-black leading-none mt-0.5">
                                  {dateObj.toLocaleDateString('default', { day: 'numeric' })}
                                </span>
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-xs font-black text-[#0f2d4a]">{formatTime12HourEST(interview.time)}</span>
                                <span className="text-[10px] font-black text-slate-400 block tracking-wider uppercase font-mono">
                                  {dateObj.toLocaleDateString('default', { weekday: 'short', year: 'numeric' })}
                                </span>
                              </div>
                            </div>

                            {/* Col 2: Opportunity & Organization */}
                            <div className="col-span-3 space-y-0.5">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-black text-[#0f2d4a] uppercase tracking-tight">
                                  {interview.opportunityTitle}
                                </h4>
                                <span className="bg-emerald-100 text-emerald-800 text-[8px] font-black px-1.5 py-0.5 rounded uppercase font-mono tracking-wider">
                                  Confirmed
                                </span>
                              </div>
                              <p className="text-xs font-bold text-[#0094c8] flex items-center gap-1">
                                <Building2 className="w-3.5 h-3.5" />
                                {interview.organizationName}
                              </p>
                            </div>

                            {/* Col 3: Candidate */}
                            <div className="col-span-2 flex items-center gap-2.5">
                              <div className="w-11 h-11 rounded-xl overflow-hidden shrink-0 border border-slate-200/90 bg-slate-50 shadow-xs flex items-center justify-center">
                                <StudentAvatar
                                  studentId={interview.studentId}
                                  studentName={interview.studentName}
                                  studentEmail={interview.studentEmail}
                                  profile={(users || []).find(u => u.uid === interview.studentId || u.email === interview.studentEmail)}
                                />
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-[10px] font-black text-slate-400 block tracking-wider uppercase font-mono leading-none">Candidate</span>
                                <div className="flex flex-col gap-1 mt-0.5">
                                  <span className="text-xs font-black text-[#0f2d4a]">
                                    {formatLastNameFirst(interview.studentName, (users || []).find(u => u.uid === interview.studentId || u.email === interview.studentEmail))}
                                  </span>
                                  {activeUser?.role === 'organization' && interview.orgScore !== undefined && (
                                    <span className="bg-blue-50 text-blue-700 text-[9px] font-black px-1.5 py-0.5 rounded inline-block w-fit uppercase tracking-wider">
                                      ★ Score: {interview.orgScore}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Col 4: Interviewer */}
                            <div className="col-span-2 flex items-center gap-2">
                              <div className="space-y-0.5">
                                <span className="text-[10px] font-black text-slate-400 block tracking-wider uppercase font-mono leading-none font-medium">Interviewer</span>
                                <span className="text-xs font-bold text-slate-700">{interview.person}</span>
                              </div>
                            </div>

                            {/* Col 5: Meeting Type & Settings */}
                            <div className="col-span-2 space-y-0.5">
                              <span className="text-[10px] font-black text-slate-400 block tracking-wider uppercase font-mono leading-none">Settings</span>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {getModeIcon(interview.mode)}
                                <span className="text-xs font-medium text-slate-600 truncate max-w-[130px]" title={getModeLabel(interview)}>
                                  {getModeLabel(interview)}
                                </span>
                              </div>
                              {activeUser?.role === 'organization' && interview.orgNotes && (
                                <span className="text-[9px] font-bold text-[#0094c8] uppercase tracking-wide block mt-1">
                                  ✎ Notes Saved
                                </span>
                              )}
                            </div>

                            {/* Col 6: Actions */}
                            <div className="col-span-1 text-right flex md:flex-col items-center justify-end gap-1.5">
                              {activeUser?.role === 'organization' && (
                                <button
                                  onClick={() => {
                                    if (editingInterviewId === interview.id) {
                                      setEditingInterviewId(null);
                                    } else {
                                      setEditingInterviewId(interview.id);
                                      setInlineNotes(interview.orgNotes || '');
                                      setInlineScore(interview.orgScore !== undefined ? interview.orgScore : '');
                                    }
                                  }}
                                  className="w-full md:w-auto px-2.5 py-1.5 bg-[#F4F7FA] hover:bg-slate-200 text-[#0f2d4a] text-[10px] font-black uppercase tracking-wider rounded-xl transition-all inline-flex items-center justify-center gap-1 border border-slate-200 cursor-pointer"
                                >
                                  <BookmarkCheck className="w-3.5 h-3.5 text-blue-600" />
                                  {editingInterviewId === interview.id ? 'Cancel' : 'Eval'}
                                </button>
                              )}
                              {interview.mode === 'virtual' && interview.link ? (
                                activeUser?.role === 'advisor' ? (
                                  <span 
                                    className="text-xs font-bold text-slate-400 uppercase tracking-wider bg-slate-100 px-3 py-1 rounded-lg border border-slate-200 inline-flex items-center gap-1 cursor-not-allowed"
                                    title="Advisor View: Virtual meeting link is reserved for the candidate and interviewer"
                                  >
                                    <Video className="w-3 h-3 text-slate-400" />
                                    Virtual
                                  </span>
                                ) : (
                                  <a 
                                    href={interview.link} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="w-full md:w-auto px-4 py-1.5 bg-[#0f2d4a] hover:bg-[#1a4066] text-white text-[10px] font-black uppercase tracking-wider rounded-xl transition-colors inline-flex items-center justify-center gap-1"
                                  >
                                    Join
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )
                              ) : (
                                <span className="text-xs font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-lg border border-slate-200">
                                  In Person
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Quiet Display of Evaluation Notes */}
                          {activeUser?.role === "organization" && interview.orgNotes && (
                            <div className="px-8 pb-3 pt-2 bg-blue-50/15 border-t border-slate-100/50 flex items-start gap-2 text-xs">
                              <span className="text-[9px] uppercase tracking-wider text-slate-400 flex items-center gap-1 mt-0.5 min-w-[120px] font-bold">
                                <Lock className="w-3 h-3 text-slate-400" /> Private Org Eval:
                              </span>
                              <span className="text-slate-600 font-medium italic flex-1">
                                "{interview.orgNotes}"
                              </span>
                            </div>
                          )}

                        </div>
                      );
                    })}
                  </div>

                </div>
              </div>
            )}

            </div>
          )}
        </>
      )}
    </div>

    </div>
  );
};

